import { useState, useEffect } from "react";
import {
  RevDialog,
  RevButton,
  RevSpinner,
  RevAvatar,
} from "@revmatics/catalyst";
import { useAppDispatch, useAppSelector } from "../../../../stores/hooks/hooks";
import {
  assignPersonasToPersonaGroup,
  fetchPersonaGroups,
} from "../../../../stores/thunks/personaGroupsThunk";
import { personaGroupType } from "../../../../stores/types/personaGroupsTypes";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedPersonaIds: number[];
  onSuccess: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    groupName: string,
    isPlatformGroup: boolean,
  ) => void;
};

export const AddToPersonaGroupDialog: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  selectedPersonaIds,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const personaGroups = useAppSelector(
    (state) => state.personaGroups.personaGroups,
  );
  const isLoading = useAppSelector((state) => state.personaGroups.isLoading);
  const isAssigning = useAppSelector(
    (state) => state.personaGroups.isAssigning,
  );

  const [selectedPersonaGroupId, setSelectedPersonaGroupId] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (isOpen && !personaGroups) {
      dispatch(fetchPersonaGroups());
    }
  }, [isOpen, personaGroups, dispatch]);

  const handleClose = () => {
    setSelectedPersonaGroupId(null);
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedPersonaGroupId || selectedPersonaIds.length === 0) return;

    // Find the selected group to pass metadata back
    const selectedGroup = personaGroups?.find(
      (g) => g.id === selectedPersonaGroupId,
    );

    if (!selectedGroup) return;

    try {
      const response = await dispatch(
        assignPersonasToPersonaGroup({
          id: selectedPersonaGroupId,
          body: { persona_ids: selectedPersonaIds },
        }),
      ).unwrap();

      await dispatch(fetchPersonaGroups());

      // Pass response and group details back to parent for alert handling
      onSuccess(response, selectedGroup.name, selectedGroup.is_platform);

      handleClose();
    } catch {
      // Error handled by thunk
    }
  };

  return (
    <RevDialog
      isOpen={isOpen}
      setIsOpen={handleClose}
      title="Add to Generation"
      size="m"
      footerComponent={
        <div className="flex justify-between items-center px-4 lg:px-6 py-4 border-t border-monochrome-100">
          <span className="typography-body-sm text-monochrome-500">
            {selectedPersonaIds.length} persona
            {selectedPersonaIds.length !== 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-3">
            <RevButton
              text="Cancel"
              buttonSize="medium"
              variant="secondary"
              handleClick={handleClose}
            />
            <RevButton
              text="Add"
              buttonSize="medium"
              handleClick={handleSubmit}
              disabled={!selectedPersonaGroupId || isAssigning}
              isLoading={isAssigning}
            />
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4 w-full min-h-[200px] overflow-y-auto">
        {isLoading && !personaGroups ? (
          <div className="flex justify-center items-center py-8">
            <RevSpinner size="medium" color="primary" centered />
          </div>
        ) : personaGroups && personaGroups.length > 0 ? (
          <div className="max-h-[300px]">
            <div className="flex flex-col p-1 space-y-3 w-full bg-primary-25 border border-monochrome-200 p-4 rounded-md ">
              {personaGroups.map((generation: personaGroupType) => {
                const isSelected = selectedPersonaGroupId === generation.id;

                return (
                  <button
                    key={generation.id}
                    onClick={() => setSelectedPersonaGroupId(generation.id)}
                    className={`flex items-center gap-3 p-2 rounded-md shadow-sm transition-all duration-200 bg-white w-full text-left outline ${
                      isSelected
                        ? "outline-2 outline-primary-500 bg-primary-25"
                        : "outline-1 outline-monochrome-200 hover:outline-primary-500"
                    }`}
                  >
                    <RevAvatar
                      variant="circle"
                      size="medium"
                      theme="light"
                      firstName={generation.name}
                    />
                    <div className="flex flex-col overflow-hidden">
                      <span className="font-medium text-monochrome-900 truncate w-full">
                        {generation.name}
                      </span>
                      <span className="typography-body-xs text-monochrome-500 truncate w-full">
                        {generation.description || "No description available"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="typography-body-sm text-monochrome-500">
              No generations available. Create a generation first.
            </p>
          </div>
        )}
      </div>
    </RevDialog>
  );
};
